import React, { useState, useEffect, useMemo, useRef } from "react";
import "./EmployeeHoursLookup.css";

const RAILWAY = process.env.REACT_APP_API_BASE || "";
const MAX_MATCHES = 8;

const fmtHours = (h) =>
  Number(h || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

// Salaried Admin/Owner staff are paid a salary, so their time lives in payroll
// rather than Teambridge shifts — their hours here may be incomplete. Flag "*".
const isSalaried = (g) => g === "Admin" || g === "Owner";
const SALARY_NOTE = "Salaried Admin and Owner staff are paid a salary — their time is tracked in payroll, not Teambridge — so these totals may be incomplete.";

export default function EmployeeHoursLookup() {
  const [people, setPeople]   = useState([]);
  const [query, setQuery]     = useState("");
  const [open, setOpen]       = useState(false);
  const [active, setActive]   = useState(0);     // highlighted match index
  const [picked, setPicked]   = useState(null);  // the chosen person {id,name,...}
  const [result, setResult]   = useState(null);  // hours-by-year payload
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const boxRef = useRef(null);

  // Load the searchable people list once.
  useEffect(() => {
    fetch(`${RAILWAY}/api/staff/hours-eligible`)
      .then((r) => r.json())
      .then((d) => setPeople(d.people || []))
      .catch(() => {});
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return people.filter((p) => p.name.toLowerCase().includes(q)).slice(0, MAX_MATCHES);
  }, [query, people]);

  const choose = (p) => {
    if (!p) return;
    setPicked(p);
    setQuery(p.name);
    setOpen(false);
    setResult(null);
    setError(null);
    setLoading(true);
    fetch(`${RAILWAY}/api/staff/hours/${encodeURIComponent(p.id)}`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setResult(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const onInput = (v) => {
    setQuery(v);
    setOpen(true);
    setActive(0);
    if (picked) { setPicked(null); setResult(null); }  // editing again clears the prior pick
  };

  const onKeyDown = (e) => {
    if (!open || !matches.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter")  { e.preventDefault(); choose(matches[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="eh-section">
      <div className="eh-head">
        <h2 className="eh-title">Employee Hours Lookup</h2>
        <p className="eh-sub">
          Hours worked by year for any <strong>Caregiver, On&nbsp;Hold, Admin, Owner, or Recruitment</strong> employee.
          Start typing a name, then pick it. The current year is to&#8209;date.
        </p>
      </div>

      <div className="eh-search" ref={boxRef}>
        <input
          className="eh-input"
          type="text"
          placeholder="Search by name…"
          value={query}
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-autocomplete="list"
          autoComplete="off"
        />
        {query && (
          <button className="eh-clear" onClick={() => { setQuery(""); setPicked(null); setResult(null); setOpen(false); }} aria-label="Clear">✕</button>
        )}
        {open && matches.length > 0 && (
          <ul className="eh-menu" role="listbox">
            {matches.map((p, i) => (
              <li
                key={p.id}
                role="option"
                aria-selected={i === active}
                className={`eh-opt ${i === active ? "active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              >
                <span className="eh-opt-name">{p.name}</span>
                <span className="eh-opt-meta">
                  {p.role && <span className={`role-chip role-${p.role.toLowerCase()}`}>{p.role}</span>}
                  <span className="eh-group">{p.group}{isSalaried(p.group) ? "*" : ""}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {open && query.trim() && matches.length === 0 && people.length > 0 && (
          <div className="eh-noresults">No match in Caregiver, On Hold, Admin, Owner, or Recruitment.</div>
        )}
      </div>

      {loading && <div className="eh-loading"><span className="spinner" /> Loading hours…</div>}
      {error && <div className="eh-error">⚠ {error}</div>}

      {!loading && !error && result && (
        <div className="eh-result">
          <div className="eh-person">
            <span className="eh-person-name">{result.name}</span>
            {result.role && <span className={`role-chip role-${result.role.toLowerCase()}`}>{result.role}</span>}
            {result.group && <span className="eh-group">{result.group}{isSalaried(result.group) ? "*" : ""}</span>}
          </div>

          {result.years.length === 0 ? (
            <div className="eh-empty">No worked shifts on record.</div>
          ) : (
            <table className="eh-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th className="num">Hours worked</th>
                  <th className="num">Shifts</th>
                </tr>
              </thead>
              <tbody>
                {result.years.map((y) => (
                  <tr key={y.year}>
                    <td>
                      <span className="eh-year">{y.year}</span>
                      {y.year === result.currentYear && <span className="eh-ytd">to date</span>}
                    </td>
                    <td className="num eh-hours">{fmtHours(y.hours)}</td>
                    <td className="num eh-shifts">{y.shifts.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num eh-hours">{fmtHours(result.totalHours)}</td>
                  <td className="num eh-shifts">{result.years.reduce((s, y) => s + y.shifts, 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          )}

          {isSalaried(result.group) && <p className="eh-note">* {SALARY_NOTE}</p>}
        </div>
      )}
    </div>
  );
}
